<script setup>
import { computed } from 'vue'
const props = defineProps(['to', 'hash', 'href', 'disabled', 'processing', 'iconLeft', 'icon', 'iconRight', 'target', 'label', 'mega', 'variant'])

const labelParts = computed(() => [props.label[0], props.label.substring(1)])
</script>

<template>
	<router-link v-if="to" :class="['btn group', { mega: mega != null }, variant]" :disabled :to>
		<div class="relative flex">
			<!-- Left Paper Tear -->
			<svg class="tear-left" width="5" height="48" viewBox="0 0 5 48" xmlns="http://www.w3.org/2000/svg" v-if="variant == null">
				<path d="M0.763077 10.752C0.572308 10.0224 0.343385 7.296 0.228923 4.7232L0 0H5.12308V48H2.28923L2.63262 46.7712C2.82338 46.08 3.12862 43.008 3.28123 39.936C3.472 36.864 3.58646 31.4112 3.58646 27.84C3.58646 22.0032 3.472 20.928 2.59446 17.664C2.02215 15.6672 1.488 13.5936 1.33538 13.056C1.22092 12.5184 0.953846 11.4816 0.763077 10.752Z" />
			</svg>

			<!-- Content -->
			<div :class="['content', { 'group-hover:!px-4': variant == 'underline' }, { 'transition-colors duration-200 group-hover:!bg-crowns-off-black group-hover:text-crowns-off-white': variant == 'social' }]">
				<i class="icon fas fa-circle-notch fa-spin group animate-spin" v-if="processing"></i>
				<template v-else>
					<div class="icon mr-2" v-if="iconLeft">
						<i :class="iconLeft"></i>
					</div>

					<!-- Left Sword -->
					<div class="left-sword md:group-hover:-mr-12" v-if="mega == 'swords'">
						<img class="h-full object-contain" src="https://cdn.papercrowns.com/website/images/elements/etches/sword-right.png" alt="Sword Right" />
					</div>

					<!-- Main Label -->
					<div class="label flex items-center whitespace-nowrap" v-if="label?.length && !mega">
						<span class="dropcap font-cabazon text-[1.3rem] leading-none">{{ labelParts[0] }}</span>
						<span class="droprest font-neue-haas-grotesk text-lg font-semibold leading-none">{{ labelParts[1] }}</span>
					</div>
					<div class="droprest" v-else>
						<slot />
					</div>

					<!-- Main Icon -->
					<div class="icon" v-if="icon">
						<i :class="icon"></i>
					</div>

					<!-- Right Sword -->
					<div class="right-sword md:group-hover:-ml-12" v-if="mega == 'swords'">
						<img class="h-full object-contain" src="https://cdn.papercrowns.com/website/images/elements/etches/sword-left.png" alt="Sword Left" />
					</div>

					<!-- Finger Pointer -->
					<div class="pointer h-full px-3 py-5" v-if="mega == 'pointer'">
						<img class="h-full -rotate-6 transition-all duration-75 md:group-hover:ml-2" src="https://cdn.papercrowns.com/website/images/elements/etches/finger.png" alt="Finger Pointing Right" />
					</div>

					<div class="icon ml-2" v-if="iconRight">
						<i :class="iconRight"></i>
					</div>
				</template>
			</div>

			<!-- Underline -->
			<svg class="absolute w-full !fill-transparent group-hover:!fill-crowns-off-white" width="146" height="7" viewBox="0 0 146 7" xmlns="http://www.w3.org/2000/svg" v-if="variant == 'underline'">
				<path
					d="M1.62844 4.92507C1.99504 5.17969 5.60863 5.4343 9.64117 5.63799C13.6737 5.79076 22.7338 5.94352 29.8039 5.99445C37.0834 6.04537 46.7196 5.73983 52.0614 5.33245C57.7698 4.87415 64.997 4.67046 70.3912 4.82323C75.314 4.976 83.9028 5.17969 89.5065 5.33245C96.786 5.53614 101.499 5.38338 106.003 4.82323C109.879 4.36492 118.258 4.05939 127.947 3.95754C138.63 3.8557 143.867 3.60109 144.443 3.24463C145.177 2.68448 145.177 2.53171 144.496 1.76787C143.763 0.902188 142.663 0.851265 129.046 1.20772C120.981 1.36049 110.507 1.86972 105.741 2.32802C98.8809 2.99001 96.3671 3.04094 93.5914 2.58263C91.4966 2.2771 83.3791 2.02248 74.2142 2.02248C65.4683 2.02248 52.6899 2.37894 45.777 2.83725C35.9836 3.44832 29.6468 3.49924 17.0778 3.1937C1.94268 2.78632 1 2.83725 1 3.65201C1 4.16123 1.31422 4.72138 1.62844 4.92507Z"
					stroke="#D8D8D8"
					stroke-width="1.00007" />
			</svg>

			<!-- Right Paper Tear -->
			<svg class="tear-right" width="3" height="48" viewBox="0 0 3 48" xmlns="http://www.w3.org/2000/svg" v-if="variant == null">
				<path d="M2.09618 42.097C2.2864 43.8956 2.62881 45.924 3.31361 47.8375C2.20907 47.9601 0 48.1318 0 47.8375V0H3.31361L3.08533 2.18139C2.9712 3.40603 2.78098 4.55413 2.6288 4.78375C2.47663 5.01337 2.24837 6.88859 2.05814 8.99344C1.90597 11.0983 1.75378 13.5093 1.71574 14.3512C1.71574 15.1932 1.90597 16.5709 2.17228 17.4128C2.47663 18.293 2.66685 20.4362 2.66685 22.5793C2.6288 24.5693 2.40054 27.7457 2.17228 29.6592C1.94401 31.5727 1.71574 34.4047 1.71574 35.9738C1.71574 37.5428 1.86791 40.2983 2.09618 42.097Z" />
			</svg>
		</div>
	</router-link>
	<component v-else :is="href ? 'a' : 'button'" :class="['btn group', { mega: mega != null }, variant]" :disabled="disabled" :target="href ? (target ?? '_blank') : null" :href>
		<div class="relative flex">
			<!-- Left Paper Tear -->
			<svg class="tear-left" width="5" height="48" viewBox="0 0 5 48" xmlns="http://www.w3.org/2000/svg" v-if="variant == null">
				<path d="M0.763077 10.752C0.572308 10.0224 0.343385 7.296 0.228923 4.7232L0 0H5.12308V48H2.28923L2.63262 46.7712C2.82338 46.08 3.12862 43.008 3.28123 39.936C3.472 36.864 3.58646 31.4112 3.58646 27.84C3.58646 22.0032 3.472 20.928 2.59446 17.664C2.02215 15.6672 1.488 13.5936 1.33538 13.056C1.22092 12.5184 0.953846 11.4816 0.763077 10.752Z" />
			</svg>

			<!-- Content -->
			<div :class="['content', { 'group-hover:!px-4': variant == 'underline' }, { 'transition-colors duration-200 group-hover:!bg-crowns-off-black group-hover:text-crowns-off-white': variant == 'social' }]">
				<i class="icon fas fa-circle-notch fa-spin group animate-spin" v-if="processing"></i>
				<template v-else>
					<div class="icon mr-2" v-if="iconLeft">
						<i :class="iconLeft"></i>
					</div>

					<!-- Left Sword -->
					<div class="left-sword md:group-hover:-mr-12" v-if="mega == 'swords'">
						<img class="h-full" src="https://cdn.papercrowns.com/website/images/elements/etches/sword-right.png" alt="Sword Right" />
					</div>

					<!-- Main Label -->
					<div class="label flex items-center whitespace-nowrap" v-if="label?.length && !mega">
						<span class="dropcap font-cabazon text-[1.3rem] leading-none">{{ labelParts[0] }}</span>
						<span class="droprest font-neue-haas-grotesk text-lg font-semibold leading-none">{{ labelParts[1] }}</span>
					</div>
					<div class="droprest" v-else>
						<slot />
					</div>

					<!-- Main Icon -->
					<div class="icon" v-if="icon">
						<i :class="icon"></i>
					</div>

					<!-- Right Sword -->
					<div class="right-sword md:group-hover:-ml-12" v-if="mega == 'swords'">
						<img class="h-full" src="https://cdn.papercrowns.com/website/images/elements/etches/sword-left.png" alt="Sword Left" />
					</div>

					<!-- Finger Pointer -->
					<div class="pointer h-full px-3 py-5" v-if="mega == 'pointer'">
						<img class="h-full -rotate-6 transition-all duration-75 md:group-hover:ml-2" src="https://cdn.papercrowns.com/website/images/elements/etches/finger.png" alt="Finger Pointing Right" />
					</div>

					<div class="icon ml-2" v-if="iconRight">
						<i :class="iconRight"></i>
					</div>
				</template>
			</div>

			<!-- Underline -->
			<svg class="absolute w-full !fill-transparent transition-all duration-300 group-hover:!fill-crowns-off-white" viewBox="0 0 146 7" xmlns="http://www.w3.org/2000/svg" v-if="variant == 'underline'">
				<path
					d="M1.62844 4.92507C1.99504 5.17969 5.60863 5.4343 9.64117 5.63799C13.6737 5.79076 22.7338 5.94352 29.8039 5.99445C37.0834 6.04537 46.7196 5.73983 52.0614 5.33245C57.7698 4.87415 64.997 4.67046 70.3912 4.82323C75.314 4.976 83.9028 5.17969 89.5065 5.33245C96.786 5.53614 101.499 5.38338 106.003 4.82323C109.879 4.36492 118.258 4.05939 127.947 3.95754C138.63 3.8557 143.867 3.60109 144.443 3.24463C145.177 2.68448 145.177 2.53171 144.496 1.76787C143.763 0.902188 142.663 0.851265 129.046 1.20772C120.981 1.36049 110.507 1.86972 105.741 2.32802C98.8809 2.99001 96.3671 3.04094 93.5914 2.58263C91.4966 2.2771 83.3791 2.02248 74.2142 2.02248C65.4683 2.02248 52.6899 2.37894 45.777 2.83725C35.9836 3.44832 29.6468 3.49924 17.0778 3.1937C1.94268 2.78632 1 2.83725 1 3.65201C1 4.16123 1.31422 4.72138 1.62844 4.92507Z"
					stroke="#D8D8D8"
					stroke-width=".5" />
			</svg>

			<!-- Right Paper Tear -->
			<svg class="tear-right" width="3" height="48" viewBox="0 0 3 48" xmlns="http://www.w3.org/2000/svg" v-if="variant == null">
				<path d="M2.09618 42.097C2.2864 43.8956 2.62881 45.924 3.31361 47.8375C2.20907 47.9601 0 48.1318 0 47.8375V0H3.31361L3.08533 2.18139C2.9712 3.40603 2.78098 4.55413 2.6288 4.78375C2.47663 5.01337 2.24837 6.88859 2.05814 8.99344C1.90597 11.0983 1.75378 13.5093 1.71574 14.3512C1.71574 15.1932 1.90597 16.5709 2.17228 17.4128C2.47663 18.293 2.66685 20.4362 2.66685 22.5793C2.6288 24.5693 2.40054 27.7457 2.17228 29.6592C1.94401 31.5727 1.71574 34.4047 1.71574 35.9738C1.71574 37.5428 1.86791 40.2983 2.09618 42.097Z" />
			</svg>
		</div>
	</component>
</template>

<style scoped>
/* Global / Default */
.btn {
	@apply h-fit w-fit touch-manipulation overflow-hidden font-cabazon text-2xl text-crowns-off-black;
}
.btn .content {
	@apply flex h-12 items-center justify-center bg-crowns-off-white px-4;
}
.btn svg {
	@apply fill-crowns-off-white;
}
.btn .icon {
	@apply text-[1.1rem];
}

/* Hover */
.btn:hover .content {
	@apply bg-crowns-castle-shadow text-crowns-off-white transition-colors duration-200 ease-in-out;
}
.btn:hover svg {
	@apply fill-crowns-castle-shadow transition-colors duration-200 ease-in-out;
}

/* Dark */
.btn.dark .content {
	@apply bg-crowns-off-black text-crowns-off-white;
}
.btn.dark svg {
	@apply fill-[#080808];
}
.btn.dark:hover .content {
	@apply bg-crowns-castle-shadow text-crowns-off-white transition-colors duration-200;
}
.btn.dark:hover svg {
	@apply fill-crowns-castle-shadow transition-colors duration-200;
}

/* Large */
.btn.large {
	@apply w-full;
}
.btn.large .content {
	@apply h-14 w-full lg:h-16;
}
.btn.large .tear-left {
	@apply -mr-1 h-14 w-3.5 lg:-mr-[4px] lg:h-16;
}
.btn.large .tear-right {
	@apply h-14 w-2 lg:h-16;
}
.btn.large .dropcap {
	@apply text-2xl;
}
.btn.large .droprest {
	@apply text-[1.25rem];
}

/* Menu Text */
.btn.menu-text .dropcap {
	@apply text-4xl uppercase leading-none xl:text-5xl;
}
.btn.menu-text .droprest {
	@apply font-alternate-gothic text-4xl uppercase leading-none xl:text-5xl;
}
.btn.menu-text:hover .content {
	@apply bg-crowns-castle-shadow text-crowns-off-white transition-colors duration-300 ease-in-out;
}
.btn.menu-text:hover svg {
	@apply fill-crowns-castle-shadow transition-colors duration-300 ease-in-out;
}

/* Social */
.btn.social .content {
	@apply flex h-10 w-10 items-center rounded-full border-2 border-crowns-off-black pt-0.5 md:h-12 md:w-12;
}
.btn.social .content .icon {
	@apply text-lg md:text-xl;
}
.btn.social.dark .content {
	@apply flex h-12 w-12 items-center rounded-full border-2 border-crowns-off-white pt-0.5 transition-colors duration-300 hover:bg-crowns-off-white hover:text-crowns-off-black;
}

/* Mega Button */
.btn.mega {
	@apply h-16 w-full md:h-20 lg:h-24;
}
.btn.mega .content {
	@apply h-16 w-full bg-[#e7e7e7] bg-[url('https://cdn.papercrowns.com/website/images/elements/button-texture.png')] bg-cover uppercase md:h-20 lg:h-24;
}
.btn.mega svg {
	@apply h-16 fill-[#e7e7e7] md:h-20 lg:h-24;
}
.btn.mega .tear-left {
	@apply w-3.5;
}
.btn.mega .tear-right {
	@apply w-2;
}
.tear-left {
	@apply -mr-1 lg:-mr-[3px];
}
.tear-right {
	@apply -ml-1 lg:-ml-[2px];
}
.btn.mega .left-sword {
	@apply z-10 h-full p-3 transition-all duration-75;
}
.btn.mega .right-sword {
	@apply z-10 h-full p-3 transition-all duration-75;
}
.btn.mega .dropcap {
	@apply text-[4.2rem] leading-none;
}
.btn.mega .droprest {
	@apply text-nowrap font-alternate-gothic text-[2.5rem] font-light leading-none md:text-5xl lg:text-6xl;
}
.btn.mega:hover .content {
	@apply text-crowns-off-black;
}

/* Underline */
.btn.underline .content {
	@apply bg-transparent text-crowns-off-white;
}
.btn.underline svg {
	@apply bottom-0;
}

/* Metro */
.btn.metro {
	@apply border border-crowns-off-white p-[1px] font-neue-haas-grotesk text-[1rem] font-semibold text-crowns-off-white transition-all duration-100 hover:border-2 hover:p-0;
}
.btn.metro .content {
	@apply h-7 whitespace-nowrap bg-crowns-dark-grey px-2 md:h-9 md:px-3;
}
.btn.metro.active {
	@apply border-2 border-crowns-off-white p-0 text-crowns-off-black;
}
.btn.metro.active .droprest {
	@apply text-crowns-off-black;
}
.btn.metro.active .content {
	@apply h-7 bg-crowns-off-white md:h-9;
}

/*  */
/* .btn:hover .icon-button-edge {
	@apply bottom-1.5 right-1.5;
}

.btn.outlined:disabled {
	@apply hover:border-default hover:text-default opacity-30;
} */
/* Variants */
/* .btn.default {
	@apply bg-paper-light hover:bg-default relative text-black;
}
.btn.outlined {
	@apply border-default hover:border-paper-darker hover:text-paper-darker relative border;
}

.btn.close {
	@apply bg-border-default h-[2rem] w-[2rem] hover:bg-neutral-500;
}
.btn:disabled {
	@apply opacity-20;
} */
</style>
